import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { postizAPI } from "../api/postizAPI";
import { getPlatformMediaError, putVideoFirst } from "../utils/platformMedia";

// ─── Platform constraints ────────────────────────────────────────────────────
// chars: max caption characters. Media rules live in utils/platformMedia.js.
const PLATFORM_LIMITS = {
  x:         { chars: 280   },
  twitter:   { chars: 280   },
  instagram: { chars: 2200  },
  facebook:  { chars: 63206 },
  linkedin:  { chars: 3000  },
  pinterest: { chars: 500   },
};

const PLATFORM_META = {
  instagram: { icon: "📷", label: "Instagram"    },
  linkedin:  { icon: "💼", label: "LinkedIn"     },
  x:         { icon: "𝕏",  label: "X (Twitter)"  },
  twitter:   { icon: "𝕏",  label: "X (Twitter)"  },
  facebook:  { icon: "f",  label: "Facebook"     },
  pinterest: { icon: "📌", label: "Pinterest"    },
};

const normalizePlatform = (v = "") => String(v).toLowerCase();

/** Strictest char limit across selected connectors */
const getEffectiveCharLimit = (selectedIds, connectors) => {
  if (!selectedIds.length) return Infinity;
  const limits = selectedIds
    .map((id) => connectors.find((c) => c.id === id))
    .filter(Boolean)
    .map((c) => PLATFORM_LIMITS[normalizePlatform(c.platform)]?.chars ?? Infinity);
  return Math.min(...limits);
};

/**
 * Validate a new file's type and size. Platform media rules are checked on the
 * whole list instead (see mediaError), since some valid posts — like a Pinterest
 * video plus its cover image — pass through an invalid state while being built.
 */
const validateNewFile = (file) => {
  const isVideo = file.type.startsWith("video/");
  const isImage = file.type.startsWith("image/");
  if (!isVideo && !isImage) return "Only image or video files are supported.";
  if (file.size > 50 * 1024 * 1024) return "File is too large (max 50 MB).";
  return null;
};

let uidCounter = 0;
const genUid = () => `m_${++uidCounter}_${Date.now()}`;
export default function SchedulePostModal({
  isOpen = false,
  onClose = null,
  isDarkMode = false,
  token = null,
  postId = null,         // if set → edit mode
  postGroup = null,      // group ID needed for "Post Now" delete+recreate in edit mode
  mediaUrl = null,
  initialCaption = "",
  initialSelectedIds = [],
  initialDate = "",
  initialTime = "09:00",
  connectors = [],
  onScheduleSuccess = null,
}) {
  const [caption, setCaption] = useState(initialCaption);
  const [selectedIds, setSelectedIds] = useState(initialSelectedIds);
  const [postNow, setPostNow] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(initialDate);
  const [scheduledTime, setScheduledTime] = useState(initialTime || "09:00");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewPlatform, setPreviewPlatform] = useState(null);
  // Each item: { uid, file, localUrl, uploadedUrl, mediaId, uploading, uploadError, isVideo }
  const [mediaList, setMediaList] = useState([]);
  // Pinterest requires a board id per connected Pinterest integration: { [integrationId]: boardId }
  const [pinterestBoardIds, setPinterestBoardIds] = useState({});
  const [pinterestBoards, setPinterestBoards] = useState({}); // { [integrationId]: [{id, name}] }
  const [pinterestBoardsLoading, setPinterestBoardsLoading] = useState({});
  const fileInputRef = useRef(null);

  // Reset form each time modal opens
  useEffect(() => {
    if (isOpen) {
      setCaption(initialCaption);
      setSelectedIds(initialSelectedIds && initialSelectedIds.length ? initialSelectedIds : []);
      setPostNow(false);
      setScheduledDate(initialDate || "");
      setScheduledTime(initialTime || "09:00");
      setError(null);
      setPreviewPlatform(null);
      setPinterestBoardIds({});
      if (mediaUrl) {
        // ── base64 data URIs from n8n ──────────────────────────────────────
        if (mediaUrl.startsWith("data:")) {
          const uid = genUid();
          setMediaList([{
            uid,
            file: null,
            localUrl: mediaUrl,
            uploadedUrl: null,
            mediaId: null,
            uploading: true,
            uploadError: null,
            isVideo: false,
          }]);

          (async () => {
            try {
              const commaIdx = mediaUrl.indexOf(",");
              const meta = mediaUrl.slice(0, commaIdx);
              const b64  = mediaUrl.slice(commaIdx + 1);
              const mimeType = meta.match(/:(.*?);/)[1];
              const ext = mimeType.split("/")[1] || "jpg";
              const byteChars = atob(b64);
              const byteArr = new Uint8Array(byteChars.length);
              for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
              const blob = new Blob([byteArr], { type: mimeType });
              const file = new File([blob], `generated-image.${ext}`, { type: mimeType });

              const result = await postizAPI.uploadMedia(token, file);
              if (result.success) {
                setMediaList((prev) =>
                  prev.map((m) =>
                    m.uid === uid
                      ? { ...m, uploadedUrl: result.url, mediaId: result.id || null, uploading: false }
                      : m
                  )
                );
              } else {
                setMediaList((prev) =>
                  prev.map((m) =>
                    m.uid === uid ? { ...m, uploading: false, uploadError: result.error } : m
                  )
                );
              }
            } catch (err) {
              setMediaList((prev) =>
                prev.map((m) =>
                  m.uid === uid ? { ...m, uploading: false, uploadError: err.message } : m
                )
              );
            }
          })();
        // ── regular hosted URL ───────────────────────────────────────────
        } else {
          setMediaList([{
            uid: genUid(),
            file: null,
            localUrl: mediaUrl,
            uploadedUrl: mediaUrl,
            mediaId: null,
            uploading: false,
            uploadError: null,
            isVideo: /\.(mp4|mov|avi|webm)(\?|$)/i.test(mediaUrl),
          }]);
        }
      } else {
        setMediaList([]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialCaption, mediaUrl,
    // Serialize the IDs array so a new array with the same contents doesn't
    // retrigger this effect and reset the form unexpectedly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(initialSelectedIds),
    initialDate, initialTime]);

  // Revoke blob URLs on unmount
  useEffect(() => {
    return () => {
      mediaList.forEach((m) => {
        if (m.localUrl?.startsWith("blob:")) URL.revokeObjectURL(m.localUrl);
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  // ─── Derived state ──────────────────────────────────────────────────────────
  const availableConnectors = connectors.filter((c) => !c.disabled && !c.refreshNeeded && !c.inBetweenSteps);
  const degradedConnectors  = connectors.filter((c) => c.disabled || c.refreshNeeded || c.inBetweenSteps);

  const effectiveCharLimit = getEffectiveCharLimit(selectedIds, connectors);

  const charCount      = caption.length;
  const isOverLimit    = effectiveCharLimit !== Infinity && charCount > effectiveCharLimit;
  const isNearLimit    = effectiveCharLimit !== Infinity && charCount > effectiveCharLimit * 0.9;
  const anyUploading   = mediaList.some((m) => m.uploading);
  const anyUploadError = mediaList.some((m) => m.uploadError);
  const imageCount     = mediaList.filter((m) => !m.isVideo).length;
  const videoCount     = mediaList.filter((m) => m.isVideo).length;

  // Edit-mode "Post Now" re-creates the post on its original platforms when none are re-selected
  const targetIds     = selectedIds.length > 0 ? selectedIds : initialSelectedIds;
  const targetsPinterest = targetIds
    .map((id) => connectors.find((c) => c.id === id))
    .some((c) => c && normalizePlatform(c.platform) === "pinterest");
  const mediaError = targetIds
    .map((id) => connectors.find((c) => c.id === id))
    .filter(Boolean)
    .map((c) => getPlatformMediaError(c.platform, mediaList))
    .find(Boolean) || null;

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    const err = validateNewFile(file);
    if (err) { setError(err); return; }
    setError(null);

    const uid = genUid();
    const localUrl = URL.createObjectURL(file);
    const isVideo = file.type.startsWith("video/");
    setMediaList((prev) => [...prev, { uid, file, localUrl, uploadedUrl: null, mediaId: null, uploading: true, uploadError: null, isVideo }]);

    const result = await postizAPI.uploadMedia(token, file);
    if (result.success) {
      setMediaList((prev) => prev.map((m) => m.uid === uid ? { ...m, uploadedUrl: result.url, mediaId: result.id || null, uploading: false } : m));
    } else {
      setMediaList((prev) => prev.map((m) => m.uid === uid ? { ...m, uploading: false, uploadError: result.error } : m));
      setError("Upload failed: " + result.error);
    }
  };

  const removeMedia = (uid) => {
    setMediaList((prev) => {
      const item = prev.find((m) => m.uid === uid);
      if (item?.localUrl?.startsWith("blob:")) URL.revokeObjectURL(item.localUrl);
      return prev.filter((m) => m.uid !== uid);
    });
  };

  const toggleConnector = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  // Lazily fetch Pinterest boards the first time a Pinterest connector is selected
  const ensurePinterestBoards = async (integrationId) => {
    if (pinterestBoards[integrationId] || pinterestBoardsLoading[integrationId]) return;
    setPinterestBoardsLoading((prev) => ({ ...prev, [integrationId]: true }));
    const result = await postizAPI.getProviderFunction(token, integrationId, 'boards');
    setPinterestBoards((prev) => ({ ...prev, [integrationId]: result.success ? (result.data || []) : [] }));
    setPinterestBoardsLoading((prev) => ({ ...prev, [integrationId]: false }));
  };

  // ─── Validation ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    if (anyUploading)                       { setError("Wait for uploads to finish.");                        return false; }
    if (anyUploadError)                     { setError("Fix upload errors before posting.");                  return false; }
    if (!postId && !caption.trim())         { setError("Please add a caption.");                              return false; }
    if (isOverLimit)                        { setError(`Caption exceeds ${effectiveCharLimit}-char limit.`);  return false; }
    if (!postId && !selectedIds.length)     { setError("Select at least one platform.");                      return false; }
    const missingBoard = selectedIds
      .map((id) => connectors.find((c) => c.id === id))
      .find((c) => c && normalizePlatform(c.platform) === "pinterest" && !pinterestBoardIds[c.id]);
    if (missingBoard)                       { setError("Select a Pinterest board before posting.");           return false; }
    // A plain reschedule only moves the date, so the media isn't re-sent
    if (mediaError && (!postId || postNow)) { setError(mediaError);                                       return false; }
    if (!postNow && !scheduledDate)         { setError("Select a date or choose Post Now.");                  return false; }
    if (!postNow && scheduledDate && new Date(`${scheduledDate}T${scheduledTime}`) <= new Date()) {
      setError("Select a future date/time."); return false;
    }
    return true;
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const buildImages = () => {
    const uploaded = mediaList.filter((m) => m.uploadedUrl && !m.uploadError);
    return (targetsPinterest ? putVideoFirst(uploaded) : uploaded)
      .map((m) => ({ id: m.mediaId || m.uploadedUrl, path: m.uploadedUrl }));
  };

  const handleSubmit = async () => {
    if (!validateForm() || !token) return;
    setLoading(true);
    setError(null);
    try {
      const publishedAt = postNow
        ? new Date().toISOString()
        : new Date(`${scheduledDate}T${scheduledTime}`).toISOString();

      // ── EDIT MODE ─────────────────────────────────────────────────────────
      if (postId) {
        if (postNow) {
          // "Post Now" in edit mode: updatePostDate only changes the date but does NOT
          // trigger the publishing pipeline. We must delete the scheduled post and
          // re-create it with type='now' so Postiz publishes it immediately.
          const groupToDelete = postGroup || postId;
          const deleteResult = await postizAPI.deletePost(token, groupToDelete);
          if (!deleteResult.success) {
            const msg = deleteResult.error || "Failed to publish post immediately.";
            setError(msg);
            toast.error(msg);
            return;
          }

          // Re-create with the original caption and media, publishing now
          const reCreateResult = await postizAPI.schedulePost(token, {
            text: caption.trim(),
            images: buildImages(),
            integrationIds: targetIds,
            integrations: connectors,
            pinterestBoardIds,
            publishedAt: new Date().toISOString(),
            postNow: true,
          });

          if (reCreateResult.success) {
            toast.success("Post published immediately! 🚀");
            if (onScheduleSuccess) onScheduleSuccess(reCreateResult.data);
            setTimeout(() => { if (onClose) onClose(); }, 1200);
          } else {
            const msg = reCreateResult.error || "Failed to publish post.";
            setError(msg);
            toast.error(msg);
          }
        } else {
          // Regular reschedule — just move the date
          const result = await postizAPI.updatePostDate(token, postId, publishedAt);
          if (result.success) {
            toast.success("Post rescheduled! 📅");
            if (onScheduleSuccess) onScheduleSuccess(result.data);
            setTimeout(() => { if (onClose) onClose(); }, 1200);
          } else {
            const msg = result.error || "Failed to reschedule post.";
            setError(msg);
            toast.error(msg);
          }
        }
        return;
      }

      // ── CREATE MODE: build media list and post ────────────────────────────
      const result = await postizAPI.schedulePost(token, {
        text: caption.trim(),
        images: buildImages(),
        integrationIds: selectedIds,
        integrations: connectors,
        pinterestBoardIds,
        publishedAt,
        postNow,
      });

      if (result.success) {
        toast.success(postNow ? "Post published! 🎉" : "Post scheduled! 📅");
        if (onScheduleSuccess) onScheduleSuccess(result.data);
        setTimeout(() => { if (onClose) onClose(); }, 1200);
      } else {
        const msg = result.error || "Failed to schedule post.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Error scheduling post.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };

  // ─── Preview ────────────────────────────────────────────────────────────────
  const renderPreview = () => {
    const connector = connectors.find((c) => c.id === previewPlatform);
    if (!connector) return null;
    const meta  = PLATFORM_META[normalizePlatform(connector.platform)] || {};
    const limit = PLATFORM_LIMITS[normalizePlatform(connector.platform)]?.chars ?? null;
    const displayText = caption || "Your post content will appear here...";
    const truncated = limit && displayText.length > limit ? displayText.slice(0, limit) + "…" : displayText;
    const firstMedia = mediaList[0];
    const images = mediaList.filter(m => !m.isVideo);
    const video  = mediaList.find(m => m.isVideo);
    return (
      <div className={`rounded-lg border-2 p-4 ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xl">{meta.icon}</span>
          <span className={`font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}>{meta.label} Preview</span>
          {limit && (
            <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${charCount > limit ? "bg-red-500 text-white" : "bg-gray-200 text-gray-700"}`}>
              {charCount}/{limit}
            </span>
          )}
        </div>
        {/* Media preview — show all images in a scrollable strip, or the video */}
        {video ? (
          <video src={video.localUrl} className="w-full max-h-40 rounded object-cover mb-3" muted />
        ) : images.length === 1 ? (
          <img src={images[0].localUrl} alt="media" className="w-full max-h-40 rounded object-cover mb-3" />
        ) : images.length > 1 ? (
          <div className="flex gap-2 overflow-x-auto pb-1 mb-3">
            {images.map((m, i) => (
              <img
                key={i}
                src={m.localUrl}
                alt={`media ${i + 1}`}
                className="h-28 w-28 flex-shrink-0 rounded object-cover border-2 border-transparent"
              />
            ))}
          </div>
        ) : null}
        <p className={`text-sm whitespace-pre-wrap ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{truncated}</p>
        <div className={`mt-3 pt-2 border-t flex gap-4 text-xs ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-200 text-gray-500"}`}>
          <span>👍 Like</span><span>💬 Comment</span><span>↗️ Share</span>
        </div>
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} style={{ backgroundColor: "rgba(0,0,0,0.55)" }} />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={`w-full max-w-2xl rounded-xl shadow-2xl max-h-[92vh] overflow-y-auto ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
            <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{postId ? "✏️ Edit Post" : "📤 Create Post"}</h2>
            <button onClick={onClose} className={`text-xl ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}>✕</button>
          </div>

          <div className="p-6 space-y-5">

            {/* Error */}
            {error && (
              <div className={`p-3 rounded-lg border border-red-500 ${isDarkMode ? "bg-red-900/30" : "bg-red-50"}`}>
                <p className={`text-sm ${isDarkMode ? "text-red-300" : "text-red-700"}`}>⚠️ {error}</p>
              </div>
            )}

            {/* Caption */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={`text-sm font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>Caption</label>
                <span className={`text-xs font-medium ${isOverLimit ? "text-red-500" : isNearLimit ? "text-amber-500" : isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {charCount}{effectiveCharLimit !== Infinity ? ` / ${effectiveCharLimit}` : ""} chars{isOverLimit && " — over limit!"}
                </span>
              </div>
              <textarea
                value={caption}
                onChange={(e) => { setCaption(e.target.value); setError(null); }}
                placeholder="Write your post content..."
                rows={5}
                className={`w-full px-4 py-3 rounded-lg border-2 text-sm resize-none transition focus:outline-none overflow-y-auto ${
                  isOverLimit ? "border-red-500"
                  : isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500"
                }`}
              />
              {isOverLimit && (
                <p className="text-xs text-red-500 mt-1">Shorten your post — exceeds {effectiveCharLimit}-char limit for selected platform(s).</p>
              )}
            </div>

            {/* Media */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={`text-sm font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                  Media
                  <span className={`ml-2 font-normal text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>(image or video · max 50 MB each)</span>
                </label>
                {mediaList.length > 0 && (
                  <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {[
                      imageCount > 0 && `${imageCount} image${imageCount !== 1 ? "s" : ""}`,
                      videoCount > 0 && `${videoCount} video${videoCount !== 1 ? "s" : ""}`,
                    ].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>

              {/* Hidden file input */}
              <input ref={fileInputRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleFileSelect} />

              {/* Thumbnail grid */}
              {mediaList.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {mediaList.map((m) => (
                    <div
                      key={m.uid}
                      className={`relative rounded-lg overflow-hidden border-2 aspect-square ${
                        m.uploadError ? "border-red-500"
                        : m.uploading  ? (isDarkMode ? "border-blue-500" : "border-blue-400")
                        : (isDarkMode  ? "border-green-600" : "border-green-400")
                      }`}
                    >
                      {m.isVideo
                        ? <video src={m.localUrl} className="w-full h-full object-cover" muted />
                        : <img src={m.localUrl} alt="" className="w-full h-full object-cover" />
                      }
                      {m.uploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                          <div className="w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                      {!m.uploading && (
                        <div className={`absolute top-1 left-1 px-1 py-0.5 rounded text-xs font-bold ${m.uploadError ? "bg-red-500 text-white" : "bg-green-500 text-white"}`}>
                          {m.uploadError ? "✕" : "✓"}
                        </div>
                      )}
                      {m.isVideo && !m.uploading && (
                        <div className="absolute bottom-1 left-1 px-1 py-0.5 bg-black/70 text-white text-xs rounded">▶</div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeMedia(m.uid)}
                        className="absolute top-1 right-1 w-5 h-5 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center text-xs"
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-dashed text-sm font-medium transition ${
                  isDarkMode
                    ? "border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400"
                    : "border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600"
                }`}
              >
                📎 {mediaList.length === 0 ? "Attach image or video" : "Add another image or video"}
              </button>

              {/* Live platform media check — also blocks submit in validateForm */}
              {mediaError && (!postId || postNow) && (
                <p className={`text-xs mt-2 ${isDarkMode ? "text-red-300" : "text-red-700"}`}>⚠️ {mediaError}</p>
              )}
            </div>

            {/* Platform selector */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={`text-sm font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>Publish to</label>
                {availableConnectors.length > 1 && (
                  <button
                    onClick={() => setSelectedIds(selectedIds.length === availableConnectors.length ? [] : availableConnectors.map((c) => c.id))}
                    className={`text-xs ${isDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700"}`}
                  >
                    {selectedIds.length === availableConnectors.length ? "Deselect all" : "Select all"}
                  </button>
                )}
              </div>

              {availableConnectors.length === 0 ? (
                <div className={`p-4 rounded-lg border-2 border-dashed text-center ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-500"}`}>
                  <p className="text-sm">No connected platforms available.</p>
                  <p className="text-xs mt-1">Go to Social Connectors to link your accounts.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {availableConnectors.map((connector) => {
                    const pid = normalizePlatform(connector.platform);
                    const meta = PLATFORM_META[pid] || { icon: "🔗", label: connector.name || connector.platform };
                    const selected = selectedIds.includes(connector.id);
                    return (
                      <button
                        key={connector.id}
                        onClick={() => {
                          toggleConnector(connector.id);
                          setError(null);
                          if (pid === "pinterest" && !selected) ensurePinterestBoards(connector.id);
                        }}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left transition ${
                          selected ? "border-blue-500 bg-blue-500/10"
                          : isDarkMode ? "border-gray-600 bg-gray-700 hover:border-gray-500"
                          : "border-gray-200 bg-white hover:border-gray-400"
                        }`}
                      >
                        <span className="text-2xl">{meta.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}>{meta.label}</p>
                          {connector.display && (
                            <p className={`text-xs truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{connector.display}</p>
                          )}
                        </div>
                        {selected && <span className="text-blue-500 text-lg font-bold">✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
              {degradedConnectors.length > 0 && (
                <p className={`text-xs mt-2 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`}>
                  ⚠️ {degradedConnectors.map((c) => PLATFORM_META[normalizePlatform(c.platform)]?.label || c.platform).join(", ")} need reconnect and can't post.
                </p>
              )}
            </div>

            {/* Pinterest board selector — required per Pinterest connector before posting */}
            {selectedIds
              .map((id) => connectors.find((c) => c.id === id))
              .filter((c) => c && normalizePlatform(c.platform) === "pinterest")
              .map((connector) => (
                <div key={connector.id}>
                  <label className={`block text-sm font-semibold mb-1.5 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                    Pinterest board {connector.display ? `(${connector.display})` : ""}
                  </label>
                  <select
                    value={pinterestBoardIds[connector.id] || ""}
                    onChange={(e) => setPinterestBoardIds((prev) => ({ ...prev, [connector.id]: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border-2 text-sm transition focus:outline-none ${isDarkMode ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"}`}
                  >
                    <option value="">
                      {pinterestBoardsLoading[connector.id] ? "Loading boards..." : "Select a board"}
                    </option>
                    {(pinterestBoards[connector.id] || []).map((board) => (
                      <option key={board.id} value={board.id}>{board.name}</option>
                    ))}
                  </select>
                </div>
              ))}

            {/* Platform preview */}
            {selectedIds.length > 0 && (
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>Preview</label>
                <div className="flex gap-2 mb-3 flex-wrap">
                  {selectedIds.map((id) => {
                    const c = connectors.find((x) => x.id === id);
                    if (!c) return null;
                    const meta = PLATFORM_META[normalizePlatform(c.platform)] || { icon: "🔗", label: c.platform };
                    return (
                      <button
                        key={id}
                        onClick={() => setPreviewPlatform(previewPlatform === id ? null : id)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                          previewPlatform === id ? "bg-blue-500 text-white border-blue-500"
                          : isDarkMode ? "bg-gray-700 text-gray-200 border-gray-600 hover:border-blue-400"
                          : "bg-gray-100 text-gray-700 border-gray-300 hover:border-blue-400"
                        }`}
                      >
                        {meta.icon} {meta.label}
                      </button>
                    );
                  })}
                </div>
                {previewPlatform && renderPreview()}
              </div>
            )}

            {/* When to post */}
            <div>
              <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>When to post</label>
              <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer mb-3 transition ${
                postNow ? "border-green-500 bg-green-500/10" : isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-gray-50"
              }`}>
                <input type="checkbox" checked={postNow} onChange={(e) => { setPostNow(e.target.checked); setError(null); }} className="w-4 h-4 accent-green-500" />
                <div>
                  <p className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>Post Now</p>
                  <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Publish immediately to all selected platforms</p>
                </div>
              </label>
              {!postNow && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Date</label>
                    <input type="date" value={scheduledDate}
                      onChange={(e) => { setScheduledDate(e.target.value); setError(null); }}
                      min={getMinDate()}
                      className={`w-full px-3 py-2 rounded-lg border-2 text-sm transition focus:outline-none ${isDarkMode ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Time</label>
                    <input type="time" value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg border-2 text-sm transition focus:outline-none ${isDarkMode ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"}`}
                    />
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Footer */}
          <div className={`sticky bottom-0 flex items-center justify-between gap-3 px-6 py-4 border-t ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
            <button
              onClick={onClose} disabled={loading}
              className={`px-5 py-2 rounded-lg font-semibold text-sm transition disabled:opacity-50 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-300" : "bg-gray-200 hover:bg-gray-300 text-gray-800"}`}
            >Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={loading || (!postId && selectedIds.length === 0) || isOverLimit || anyUploading}
              className="px-6 py-2 rounded-lg font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white transition disabled:opacity-50"
            >
              {loading
                ? (postNow ? "Publishing..." : postId ? "Saving..." : "Scheduling...")
                : postId
                  ? "Save Changes 📅"
                  : postNow ? "Publish Now 🚀" : "Schedule Post 📅"
              }
            </button>
          </div>
        </div>
      </div>
    </>
  );
}